import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { useModules } from '@contexts/modules-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { navigateToHelp } from '@shared/helpers/navigate-to-help/navigateToHelp';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { GuardState } from './useOrganizationGuard.types';

/**
 * Manages all state and side effects for OrganizationRouteGuard.
 *
 * Responsibilities:
 * 1. Auto-select Manage module if not already selected
 * 2. Verify user has Manage module access
 * 3. Wait for organizations to load (enabled after Manage is selected)
 * 4. Auto-select organization based on URL org ID
 * 5. Handle org switch failures with navigate-back and deferred flag message
 */
export function useOrganizationGuard(): GuardState {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const {
    modules,
    selectedModule,
    isModulesLoading,
    isModulesError,
    selectModule,
  } = useModules();
  const {
    organizations,
    selectedOrganization,
    isOrganizationsLoading,
    isOrganizationsError,
    isSyncingOrganization,
    syncOrganizationFromUrl,
  } = useOrganizations();

  const [isAutoSelectingModule, setIsAutoSelectingModule] =
    React.useState(false);

  const attemptedModuleSelectionRef = React.useRef(false);
  const attemptedCustomerIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (isAutoSelectingModule || attemptedModuleSelectionRef.current) return;
    if (isModulesLoading || isModulesError || !modules) return;

    const hasManageAccess = modules.some(m => m.name === 'Manage');
    if (!hasManageAccess || selectedModule?.name === 'Manage') return;

    attemptedModuleSelectionRef.current = true;
    setIsAutoSelectingModule(true);

    selectModule('Manage')
      .then(() => {
        setIsAutoSelectingModule(false);
      })
      .catch(() => {
        setIsAutoSelectingModule(false);
        emitFlagMessage(
          'danger',
          'Failed to switch to Manage module. Please try again.',
          5000
        );
      });
  }, [
    modules,
    selectedModule,
    isModulesLoading,
    isModulesError,
    isAutoSelectingModule,
    selectModule,
  ]);

  // Sync organization from URL — URL is the source of truth.
  // URL contains customerId; lookup finds the org, then API uses org.id.
  // Sets cache immediately for instant UI feedback, then awaits the PUT.
  // On PUT failure: rolls back to previous org, navigates back, and emits flag.
  React.useEffect(() => {
    // Allow retrying when the URL customer ID changes
    if (
      attemptedCustomerIdRef.current !== null &&
      customerId !== attemptedCustomerIdRef.current
    ) {
      attemptedCustomerIdRef.current = null;
    }

    if (attemptedCustomerIdRef.current === customerId) return;
    if (isAutoSelectingModule || selectedModule?.name !== 'Manage') return;
    if (
      isOrganizationsLoading ||
      isOrganizationsError ||
      !organizations ||
      !customerId
    )
      return;

    const targetOrg = organizations.find(org => org.customerId === customerId);
    if (!targetOrg || selectedOrganization?.customerId === customerId) return;

    const previousOrgCustomerId = selectedOrganization?.customerId;

    attemptedCustomerIdRef.current = customerId;

    syncOrganizationFromUrl(targetOrg.id).catch(() => {
      // Do NOT reset attemptedCustomerIdRef here — the rollback setQueryData triggers
      // a re-render before navigate takes effect. If we cleared the ref, the
      // intermediate render (customerId still = target, selectedOrg = rolled-back)
      // would pass the guard and re-fire the sync, causing an infinite loop.
      // The ref resets naturally when the URL org changes (first check above).

      // Navigate back to the previous org (or org selector if none)
      const fallbackPath = previousOrgCustomerId
        ? `/manage/${previousOrgCustomerId}/home`
        : '/organization-selector';
      navigate(fallbackPath, { replace: true });

      // Defer flag emission: the rollback unmounts CobaltFlagMessage (guard
      // switches to "loading" before navigate settles), so emitting now would
      // be lost. setTimeout lets React flush the navigate, re-mount the flag
      // container, and only then show the notification.
      setTimeout(() => {
        emitFlagMessage(
          'danger',
          `Something went wrong. Please try again.`,
          5000,
          true,
          'Get help',
          navigateToHelp
        );
      }, 0);
    });
  }, [
    customerId,
    organizations,
    selectedOrganization,
    selectedModule,
    isOrganizationsLoading,
    isOrganizationsError,
    isAutoSelectingModule,
    syncOrganizationFromUrl,
    navigate,
  ]);

  // --- State resolution ---

  if (isModulesLoading) {
    return { status: 'loading', message: 'Loading...' };
  }

  if (isModulesError) {
    return { status: 'error' };
  }

  const hasManageAccess = modules?.some(m => m.name === 'Manage');
  if (!hasManageAccess) {
    return { status: 'not-found' };
  }

  if (isAutoSelectingModule || selectedModule?.name !== 'Manage') {
    return { status: 'loading', message: 'Loading Manage module...' };
  }

  if (isOrganizationsLoading) {
    return { status: 'loading', message: 'Loading organizations...' };
  }

  // While syncing org from URL, cache is transitional — keep showing loader
  // to prevent transient error/not-found states from flashing the error page.
  if (isSyncingOrganization) {
    return { status: 'loading', message: 'Switching organization...' };
  }

  if (isOrganizationsError) {
    return { status: 'error' };
  }

  const targetOrg = organizations?.find(org => org.customerId === customerId);
  if (!targetOrg) {
    return { status: 'not-found' };
  }

  if (selectedOrganization?.customerId !== customerId) {
    return { status: 'loading', message: 'Loading organization...' };
  }

  return { status: 'ready' };
}
